import { zillowApi } from "api/zillow";
import { zumperApi } from "api/zumper";
import { dwellsyApi } from "api/dwellsy";
import { apartmentsComApi } from "api/apartmentsCom";
import { mhvillageApi } from "api/mhvillage";
import { metaAdsApi } from "api/meta-ads";
import { QUERY_META_ADS_STATUS } from "views/settings-integration/integrations/meta-ads/constants";
import { boomFinanceApi } from "api/boom-finance";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Radio } from "shared/components";
import { Card } from "shared/components/Card";
import GoogleAnalyticsIcon from "shared/icons/brands/GoogleAnalytics-brand-icon.svg?react";
import HubSpotIcon from "shared/icons/brands/hubspot.svg?react";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  IntegrationId,
  Integration,
  Step,
  DIRECT_CONNECT_INTEGRATIONS
} from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "views/settings-integration/integrations/pms/constants";
import { PropertyManagementSystem } from "shared/types/partner";
import { FEATURES, Partner } from "types/landlord/partner";

interface FirstStepProps {
  selectedIntegration: PropertyManagementSystem | IntegrationId;
  setSelectedIntegration: (
    id: PropertyManagementSystem | IntegrationId
  ) => void;
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
}

const BASE_INTEGRATIONS: Integration[] = [
  {
    id: IntegrationId.GA,
    name: "Google Analytics",
    icon: <GoogleAnalyticsIcon />
  },
  {
    id: IntegrationId.HUBSPOT,
    name: "HubSpot",
    icon: <HubSpotIcon />
  }
];

const PMS_INTEGRATIONS: Integration[] = [
  {
    id: PropertyManagementSystem.ManageAmerica,
    name: PMSNames[PropertyManagementSystem.ManageAmerica],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.ManageAmerica}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Buildium,
    name: PMSNames[PropertyManagementSystem.Buildium],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Buildium} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Appfolio,
    name: PMSNames[PropertyManagementSystem.Appfolio],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Appfolio} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Yardi,
    name: PMSNames[PropertyManagementSystem.Yardi],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Yardi} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentManager,
    name: PMSNames[PropertyManagementSystem.RentManager],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentManager}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Resman,
    name: PMSNames[PropertyManagementSystem.Resman],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Resman} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Rentvine,
    name: PMSNames[PropertyManagementSystem.Rentvine],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Rentvine} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Quext,
    name: PMSNames[PropertyManagementSystem.Quext],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Quext} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Entrata,
    name: PMSNames[PropertyManagementSystem.Entrata],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Entrata} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentersWarehouse,
    name: PMSNames[PropertyManagementSystem.RentersWarehouse],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentersWarehouse}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Propertyware,
    name: PMSNames[PropertyManagementSystem.Propertyware],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.Propertyware}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Revela,
    name: PMSNames[PropertyManagementSystem.Revela],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Revela} size="BASE" />
    )
  }
];

type PmsCapability = {
  requiresBoomScreen: boolean;
  requiresBoomReport: boolean; // boom_report maps to isRentReporting in portal
};

// Maps each PMS to the product features that grant access to it.
// boom_screen = FEATURES.BOOM_SCREEN, boom_report = FEATURES.RENT_REPORTING
const PMS_CAPABILITIES: Record<string, PmsCapability> = {
  // boom_screen only
  [PropertyManagementSystem.Propertyware]: {
    requiresBoomScreen: true,
    requiresBoomReport: false
  },
  // boom_report only
  [PropertyManagementSystem.Resman]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Entrata]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Quext]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  // both boom_screen and boom_report grant access
  [PropertyManagementSystem.Buildium]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentManager]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Yardi]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Appfolio]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Rentvine]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentersWarehouse]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.ManageAmerica]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  // boom_report only
  [PropertyManagementSystem.Revela]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  }
};

const filterPmsIntegrationsByProducts = (
  integrations: Integration[],
  {
    isBoomScreen,
    isBoomReport
  }: { isBoomScreen: boolean; isBoomReport: boolean }
): Integration[] =>
  integrations.filter((integration) => {
    const cap = PMS_CAPABILITIES[integration.id];
    if (!cap) return false;
    return (
      (cap.requiresBoomScreen && isBoomScreen) ||
      (cap.requiresBoomReport && isBoomReport)
    );
  });

const IntegrationRow = ({
  integration,
  selectedIntegration,
  disabled,
  onSelect
}: {
  integration: Integration;
  selectedIntegration: string;
  disabled: boolean;
  onSelect: () => void;
}) => (
  <Card
    className={cx(
      "p-6 flex justify-between m-1",
      disabled && "opacity-50 bg-alpha-40"
    )}
    onClick={() => {
      if (!disabled) onSelect();
    }}
  >
    <div className="flex items-center gap-4">
      {integration.icon}
      <p className="font-bold text-base">{integration.name}</p>
    </div>
    <Radio
      className="mr-4 self-center cursor-pointer"
      id={integration.id}
      name={integration.name}
      onChange={() => null}
      checked={selectedIntegration === integration.id}
    />
  </Card>
);

const buildOthersIntegrations = ({
  isBoomCRM,
  isBoomScreen,
  partner,
  isMetaAdsConnected,
  isBoomFinanceEnabled
}: {
  isBoomCRM: boolean;
  isBoomScreen: boolean;
  partner: Partner | null | undefined;
  isMetaAdsConnected: boolean;
  isBoomFinanceEnabled: boolean;
}): Integration[] => {
  const optionalIntegrations: { show: boolean; integration: Integration }[] = [
    {
      show: isBoomCRM && !partner?.codebox_connected,
      integration: {
        id: IntegrationId.CODEBOX,
        name: "Codebox",
        icon: <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !partner?.sentrilock_connected,
      integration: {
        id: IntegrationId.SENTRILOCK,
        name: "SentriLock",
        icon: <IntegrationLogoIcon id={IntegrationId.SENTRILOCK} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !partner?.zillow_connected,
      integration: {
        id: IntegrationId.ZILLOW,
        name: "Zillow",
        icon: <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !partner?.zumper_connected,
      integration: {
        id: IntegrationId.ZUMPER,
        name: "Zumper",
        icon: <IntegrationLogoIcon id={IntegrationId.ZUMPER} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !partner?.dwellsy_connected,
      integration: {
        id: IntegrationId.DWELLSY,
        name: "Dwellsy",
        icon: <IntegrationLogoIcon id={IntegrationId.DWELLSY} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !partner?.apartments_com_connected,
      integration: {
        id: IntegrationId.APARTMENTS_COM,
        name: "Apartments.com",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.APARTMENTS_COM} size="BASE" />
        )
      }
    },
    {
      show: isBoomCRM && !partner?.mhvillage_connected,
      integration: {
        id: IntegrationId.MHVILLAGE,
        name: "MHVillage",
        icon: <IntegrationLogoIcon id={IntegrationId.MHVILLAGE} size="BASE" />
      }
    },
    {
      show: isBoomCRM && !isMetaAdsConnected,
      integration: {
        id: IntegrationId.META_ADS,
        name: "Meta Lead Ads",
        icon: <IntegrationLogoIcon id={IntegrationId.META_ADS} size="BASE" />
      }
    },
    {
      show: isBoomScreen && isBoomFinanceEnabled,
      integration: {
        id: IntegrationId.BOOM_FINANCE,
        name: "BoomFinance",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.BOOM_FINANCE} size="BASE" />
        )
      }
    }
  ];

  return [
    ...BASE_INTEGRATIONS,
    ...optionalIntegrations
      .filter((option) => option.show)
      .map((option) => option.integration)
  ];
};

export const FirstStep = ({
  selectedIntegration,
  setSelectedIntegration,
  setCurrentStep,
  closeModal
}: FirstStepProps) => {
  const { currentSettings, hubspotHubId } = useSettingsController();
  const { type, partner, refreshPartnerData } = useAuthContext();
  const isBoomFinanceEnabled = partner?.features.includes(
    FEATURES.WIP_BOOM_FINANCE
  );

  const availablePmsIntegrations = useMemo(
    () =>
      filterPmsIntegrationsByProducts(PMS_INTEGRATIONS, {
        isBoomScreen: type.isBoomScreen,
        isBoomReport: type.isRentReporting
      }).sort((a, b) => a.name.localeCompare(b.name)),
    [type.isBoomScreen, type.isRentReporting]
  );

  const { mutate: connectZillow, isPending: isConnectingZillow } = useMutation({
    mutationKey: ["ZILLOW", "CREATE"],
    mutationFn: () => zillowApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: connectZumper, isPending: isConnectingZumper } = useMutation({
    mutationKey: ["ZUMPER", "CREATE"],
    mutationFn: () => zumperApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: connectDwellsy, isPending: isConnectingDwellsy } =
    useMutation({
      mutationKey: ["DWELLSY", "CREATE"],
      mutationFn: () => dwellsyApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectApartmentsCom, isPending: isConnectingApartmentsCom } =
    useMutation({
      mutationKey: ["APARTMENTS_COM", "CREATE"],
      mutationFn: () => apartmentsComApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectMhvillage, isPending: isConnectingMhvillage } =
    useMutation({
      mutationKey: ["MHVILLAGE", "CREATE"],
      mutationFn: () => mhvillageApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { data: metaAdsStatus } = useQuery({
    queryKey: QUERY_META_ADS_STATUS,
    queryFn: metaAdsApi.getStatus,
    enabled: type.isBoomCRM
  });

  const { mutate: startMetaAdsOauth, isPending: isStartingMetaAdsOauth } =
    useMutation({
      mutationKey: ["META_ADS", "AUTHORIZATION_URL"],
      mutationFn: metaAdsApi.getAuthorizationUrl,
      onSuccess: ({ authorization_url }) => {
        window.location.assign(authorization_url);
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectBoomFinance, isPending: isConnectingBoomFinance } =
    useMutation({
      mutationKey: ["BOOM_FINANCE", "CREATE"],
      mutationFn: () => boomFinanceApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const othersIntegrations = buildOthersIntegrations({
    isBoomCRM: type.isBoomCRM,
    isBoomScreen: type.isBoomScreen,
    partner,
    isMetaAdsConnected: Boolean(metaAdsStatus?.connected),
    isBoomFinanceEnabled: Boolean(isBoomFinanceEnabled)
  });

  const connectActions: Partial<
    Record<PropertyManagementSystem | IntegrationId, () => void>
  > = {
    [IntegrationId.ZILLOW]: connectZillow,
    [IntegrationId.ZUMPER]: connectZumper,
    [IntegrationId.DWELLSY]: connectDwellsy,
    [IntegrationId.APARTMENTS_COM]: connectApartmentsCom,
    [IntegrationId.MHVILLAGE]: connectMhvillage,
    [IntegrationId.META_ADS]: startMetaAdsOauth,
    [IntegrationId.BOOM_FINANCE]: connectBoomFinance
  };

  const handleContinue = () => {
    const id = selectedIntegration as PropertyManagementSystem | IntegrationId;
    const connectAction = connectActions[id];
    if (connectAction) connectAction();
    else if (DIRECT_CONNECT_INTEGRATIONS.has(id)) return;
    else setCurrentStep(Step.SECOND);
  };

  const isDisabled = (id: string) => {
    if (id === IntegrationId.GA)
      return Boolean(currentSettings.google.googleAnalyticsId);
    if (id === IntegrationId.HUBSPOT) return Boolean(hubspotHubId);
    return false;
  };

  if (type.isMultiPMS)
    return (
      <>
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto gap-4">
          {availablePmsIntegrations.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-content-700">
                Property management system
              </p>
              <div className="flex flex-col gap-2">
                {availablePmsIntegrations.map((integration) => (
                  <IntegrationRow
                    key={integration.id}
                    integration={integration}
                    selectedIntegration={selectedIntegration}
                    disabled={false}
                    onSelect={() =>
                      setSelectedIntegration(
                        integration.id as
                          | PropertyManagementSystem
                          | IntegrationId
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-content-700">Others</p>
            <div className="flex flex-col gap-2">
              {othersIntegrations.map((integration) => (
                <IntegrationRow
                  key={integration.id}
                  integration={integration}
                  selectedIntegration={selectedIntegration}
                  disabled={isDisabled(integration.id)}
                  onSelect={() =>
                    setSelectedIntegration(
                      integration.id as PropertyManagementSystem | IntegrationId
                    )
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleContinue}
          disabled={!selectedIntegration}
          loading={
            isConnectingZillow ||
            isConnectingZumper ||
            isConnectingDwellsy ||
            isConnectingApartmentsCom ||
            isConnectingMhvillage ||
            isConnectingBoomFinance ||
            isStartingMetaAdsOauth
          }
          className="ml-auto mt-4"
        >
          Continue
        </Button>
      </>
    );

  // Legacy flow
  return (
    <>
      <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
        {othersIntegrations.map((integration) => {
          const disabled = isDisabled(integration.id);
          return (
            <Card
              className={cx(
                "p-6 flex justify-between m-1 shrink-0",
                disabled && "opacity-50 bg-alpha-40"
              )}
              key={integration.id}
              onClick={() => {
                if (!disabled)
                  setSelectedIntegration(
                    integration.id as PropertyManagementSystem | IntegrationId
                  );
              }}
            >
              <div className="flex items-center gap-4">
                {integration.icon}
                <p className="font-bold text-base">{integration.name}</p>
              </div>
              <Radio
                className="mr-4 self-center cursor-pointer"
                id={integration.id}
                name={integration.name}
                onChange={() => null}
                checked={selectedIntegration === integration.id}
              />
            </Card>
          );
        })}
      </div>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={handleContinue}
        disabled={!selectedIntegration}
        loading={
          isConnectingZillow ||
          isConnectingZumper ||
          isConnectingDwellsy ||
          isConnectingApartmentsCom ||
          isConnectingMhvillage ||
          isConnectingBoomFinance ||
          isStartingMetaAdsOauth
        }
        className="ml-auto mt-4"
      >
        Continue
      </Button>
    </>
  );
};
